import {useContext, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {Box} from '@mui/material';


import {DreamJob} from './DreamJob';
import {Editable} from './Editable';
import {LeadershipExperience} from './LeadershipExperience';
import {ProfileSectionCard} from './ProfileSectionCard';
import Settings from './Settings';
import {StartDate} from './StartDate';
import {EmpionLabeledValueList} from '../../../../../components/EmpionLabeledValueList/EmpionLabeledValueList';
import {LanguageContext} from '../../../../../contexts/LanguageContext/LanguageContext';
import {LanguageProficiency} from '../../../../../domain/languages';
import {ApplicantSkill} from '../../../../../types/applicantSkill/ApplicantSkill';
import {TrackRecord} from '../../../../../types/shared-types';
import {formatProfessionalExperienceDisplay} from '../../../../../utils/formatProfessionalExpDisplay';
import * as Types from '../../types';
import {ExperienceMetadata} from '../../types';

interface Props {
  isMobile?: boolean;
  settings: Types.ProfileSettings;
  skills: ApplicantSkill[];
  languages: {
    id: string;
    proficiency: LanguageProficiency;
  }[];
  dreamJobDescription: string | null;
  professionalExperience: TrackRecord<ExperienceMetadata>[];
  leadershipExperience: boolean;
  earliestStartDate?: string;
  onEdit?: (formType: Types.ProfileFormType) => void;
}

const SideMenu = ({
  isMobile,
  settings,
  onEdit,
  dreamJobDescription,
  skills,
  languages,
  professionalExperience,
  leadershipExperience,
  earliestStartDate,
}: Props) => {
  const {t} = useTranslation();
  const {t: td} = useTranslation('domain');
  const title = t('applicantProfile.skills.title');
  const languagesTitle = t('applicantProfile.languages.title');
  const professionalExperienceTitle = t('applicantProfile.professionalExperience.title');
  const currentLanguage = useContext(LanguageContext);

  const sectionGroups: Types.SectionGroup[] = useMemo(() => {
    return [
      {
        title: title,
        formType: 'skills',
        groups: [
          {
            color: 'secondary',
            items: (skills || []).map((skill) => ({
              id: skill.resourceId,
              label: skill.standardSkill.name[currentLanguage],
            })),
          },
        ],
      },
      {
        title: languagesTitle,
        formType: 'languages',
        groups: [
          {
            color: 'primary',
            items: (languages || []).map((language) => ({
              id: language.id,
              label: td(`languageEnum.${language.id}`),
              value: language.proficiency,
            })),
          },
        ],
      },
      {
        title: professionalExperienceTitle,
        formType: 'experience',
        groups: [
          {
            color: 'primary',
            items: (professionalExperience || []).map((exp) =>
              formatProfessionalExperienceDisplay(exp, currentLanguage)
            ),
          },
        ],
      },
    ];
  }, [
    currentLanguage,
    languages,
    languagesTitle,
    professionalExperience,
    professionalExperienceTitle,
    skills,
    td,
    title,
  ]);

  return (
    <Box sx={{display: 'flex', flexDirection: 'column', gap: 3}}>
      <ProfileSectionCard sx={{display: isMobile ? 'none' : 'block'}}>
        <Settings data={settings} onEdit={onEdit} testid={'edit-settings-desktop'} />
      </ProfileSectionCard>
      <ProfileSectionCard>
        <LeadershipExperience onEdit={onEdit} leadershipExperience={leadershipExperience} />
      </ProfileSectionCard>
      {sectionGroups.map((group, idx) => (
        <ProfileSectionCard key={idx}>
          <Editable onEdit={() => onEdit?.(group.formType)} testid={`edit-${group.formType}`}>
            <EmpionLabeledValueList
              title={group.title}
              groups={group.groups}
              sx={{
                mt: 3,
              }}
            />
          </Editable>
        </ProfileSectionCard>
      ))}
      <ProfileSectionCard>
        <DreamJob onEdit={onEdit} dreamJobDescription={dreamJobDescription} />
      </ProfileSectionCard>
      <ProfileSectionCard>
        <StartDate onEdit={onEdit} startDate={earliestStartDate} />
      </ProfileSectionCard>
    </Box>
  );
};

export default SideMenu;
